import CancelIcon from '@mui/icons-material/Cancel';
import SaveIcon from '@mui/icons-material/Save';
import Warning from '@mui/icons-material/Warning';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Tooltip, Typography } from '@mui/material';
import { MEIO_JULGAMENTO_PROCESSO } from '@/dominio/MeioJulgamentoProcesso';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { inject, observer } from 'mobx-react';
import { Fragment } from 'react';
import '../../css/botao.css';
import Configuracao from '../../util/Configuracao';
import AutoComplete from '../AutoComplete';
import ReactComponent from '../ReactComponent';
import ModalConfirmacao from './ModalConfirmacao';

class ProcuradorCertidao extends ReactComponent {
    aberto = this.observableProps.open || false;
    opcoesProcuradoresSIJ = [];
    opcoesProcuradoresPJe = [];
    procurador;
    modalConfirmacaoProcuradorOpen = false;

    constructor(props) {
        super(props);

        makeObservable(this, {
            aberto: observable,
            opcoesProcuradoresSIJ: observable,
            opcoesProcuradoresPJe: observable,
            procurador: observable,
            modalConfirmacaoProcuradorOpen: observable,
            opcoesProcuradores: computed,
            configurarProcurador: action,
            callbackAutoComplete: action,
            handleClickProcurador: action,
            handleClickCancelar: action,
            handleClickConfirmar: action,
            callbackCancelar: action,
            callbackConfirmarProcurador: action,
            valorInicialAutoComplete: computed,
            tipoJulgamentoVirtual: computed,
            procuradorProcesso: computed,
            alertaProcuradorSessao: computed,
            alertaProcuradorProcesso: computed,
            alertaProcurador: computed,
            cargoProcuradorProcesso: computed,
            bloquearEdicaoCertidao: computed,
            bloquearEdicaoProcurador: computed,
            existePresidenteComposicao: computed
        });
    }

    get registro() {
        return this.observableProps.registro;
    }

    get opcoesProcuradores() {
        if (this.registro.processo.origem === Configuracao.ORIGEM_PJE) {
            return this.converterOpcoesProcuradores(this.dominioStore.procuradoresPJe);
        }
        return this.converterOpcoesProcuradores(this.dominioStore.procuradoresSIJ);
    }

    converterOpcoesProcuradores = (procuradores) => {
        return procuradores.map((procurador) => {
            return {
                texto: procurador.codigo + ' - ' + procurador.nome,
                textoSecundario: procurador.descricaoClasse,
                valor: procurador
            };
        });
    };

    async configurarProcurador() {
        let procurador = this.registro.procurador;
        if (!procurador && this.registro.processo.origem === Configuracao.ORIGEM_PJE) {
            procurador = await this.sessaoService.recuperarProcurador(this.registro.sessaoId);
            runInAction(() => (this.registro.procurador = procurador));
        }
        runInAction(() => (this.procurador = this.registro.procurador));
    }

    callbackAutoComplete = (option) => {
        this.procurador = option ? option.valor : null;
    };

    handleClickProcurador = () => {
        this.configurarProcurador();
        this.aberto = true;
    };

    handleClickCancelar = () => {
        this.aberto = false;
        this.observableProps.handleClose && this.observableProps.handleClose();
    };

    handleClickConfirmar = () => {
        if (this.observableProps.assinada) {
            this.modalConfirmacaoProcuradorOpen = true;
        } else this.callbackConfirmarProcurador();
    };

    callbackCancelar = () => {
        this.modalConfirmacaoProcuradorOpen = false;
    };

    callbackConfirmarProcurador = () => {
        this.modalConfirmacaoProcuradorOpen = false;
        if (this.procurador) {
            this.observableProps.callbackAlterarProcurador(this.procurador);
            this.aberto = false;
            this.observableProps.handleClose && this.observableProps.handleClose();
        }
    };

    renderModalConfirmaProcurador() {
        if (this.observableProps.assinada) {
            return (
                <ModalConfirmacao
                    open={this.modalConfirmacaoProcuradorOpen}
                    callbackConfirmar={this.callbackConfirmarProcurador}
                    callbackCancelar={this.callbackCancelar}
                    titulo={'Desbloquear assinatura?'}
                >
                    <Typography>Esta alteração vai desbloquear a assinatura. Confirma ?</Typography>
                </ModalConfirmacao>
            );
        } else return null;
    }

    get valorInicialAutoComplete() {
        return this.procurador && this.procurador.codigo !== 0
            ? this.procurador.codigo + ' - ' + this.procurador.nome
            : null;
    }

    get tipoJulgamentoVirtual() {
        return this.registro.tipoJulgamento === MEIO_JULGAMENTO_PROCESSO.VIRTUAL.codigo;
    }

    get procuradorProcesso() {
        return this.registro?.procurador;
    }

    get alertaProcuradorSessao() {
        let sessao = this.salaSessaoStore.getSessaoSessaoSelecionada(this.registro.sessaoId);
        return (
            !this.tipoJulgamentoVirtual &&
            sessao.procurador &&
            this.procuradorProcesso &&
            sessao.procurador.codigo !== this.procuradorProcesso.codigo
        );
    }

    get alertaProcuradorProcesso() {
        return (
            !this.tipoJulgamentoVirtual &&
            this.registro.conteudoCertidao &&
            this.procuradorProcesso?.nome &&
            (this.registro.conteudoCertidao.toUpperCase().indexOf(this.procuradorProcesso.nome.trim().toUpperCase()) ===
                -1 ||
                this.registro.conteudoCertidao.toUpperCase().indexOf(this.cargoProcuradorProcesso.toUpperCase()) === -1)
        );
    }

    get alertaProcurador() {
        return this.alertaProcuradorSessao || this.alertaProcuradorProcesso;
    }

    get cargoProcuradorProcesso() {
        let retorno = '';
        if (this.procuradorProcesso) {
            let parametro = this.parametroStore.getParametro(
                this.procuradorProcesso.descricaoClasse + '_' + this.procuradorProcesso.genero
            );
            retorno = parametro ? parametro.valor : '';
        }
        return retorno;
    }

    get bloquearEdicaoCertidao() {
        return (
            this.registro.certidaoJulgamentoTransferida &&
            !this.registro.informacoesAdicionais.permitirQuebraAssinaturaCertidao
        );
    }

    get bloquearEdicaoProcurador() {
        return !this.existePresidenteComposicao || this.tipoJulgamentoVirtual || this.bloquearEdicaoCertidao;
    }

    get existePresidenteComposicao() {
        return (
            this.registro &&
            this.registro.composicao.findIndex((composicao) => composicao.magistrado.presidenteSessao === true) !== -1
        );
    }

    render() {
        if (!this.isComponenteHabilitado('ProcuradorCertidao')) {
            return null;
        }

        return (
            <Fragment>
                {!this.observableProps.ocultarIcone && (
                    <Tooltip
                        placement="top"
                        title={this.renderTooltip()}
                    >
                        <div>
                            <Button
                                variant={'outlined'}
                                color="primary"
                                size="small"
                                style={{ height: '32px' }}
                                onClick={this.handleClickProcurador}
                                disabled={this.bloquearEdicaoProcurador}
                            >
                                {'Procurador'}
                                {this.alertaProcurador && <Warning style={{ fill: 'darkorange' }} />}
                            </Button>
                        </div>
                    </Tooltip>
                )}
                {this.aberto && (
                    <Dialog
                        open={this.aberto}
                        maxWidth="xl"
                    >
                        <DialogTitle>Editar Procurador</DialogTitle>
                        <DialogContent style={{ paddingTop: '10px', width: '500px' }}>
                            <AutoComplete
                                apresentarTextoSecundario
                                value={this.valorInicialAutoComplete}
                                options={this.opcoesProcuradores}
                                onChange={this.callbackAutoComplete}
                                label={'Procurador'}
                            />
                        </DialogContent>
                        <DialogActions>
                            {this.renderModalConfirmaProcurador()}
                            <Button
                                onClick={this.handleClickCancelar}
                                color="secondary"
                                variant="outlined"
                                endIcon={<CancelIcon />}
                            >
                                Cancelar
                            </Button>
                            <Button
                                onClick={this.handleClickConfirmar}
                                color="primary"
                                variant="outlined"
                                endIcon={<SaveIcon />}
                                disabled={!this.procurador}
                            >
                                Salvar
                            </Button>
                        </DialogActions>
                    </Dialog>
                )}
            </Fragment>
        );
    }

    renderTooltip() {
        if (this.bloquearEdicaoCertidao) {
            return <Typography>Certidão já enviada para o {this.registro.processo.origem}</Typography>;
        }

        if (!this.existePresidenteComposicao) {
            return <Typography>Defina o presidente da composição para alteração do procurador.</Typography>;
        }

        let mensagens = [];
        if (this.alertaProcuradorSessao) {
            mensagens.push(
                'Os dados de Procurador selecionados para este processo são diferentes dos dados do Procurador padrão da sessão'
            );
        } else if (this.alertaProcuradorProcesso) {
            mensagens.push(
                'Os dados de Procurador informados nesta certidão são diferentes dos dados do Procurador selecionado'
            );
        }

        if (mensagens.length > 0) {
            return (
                <Box>
                    <Box>
                        <Typography sx={{ fontWeight: 'bold' }}>Atenção:</Typography>
                    </Box>
                    {mensagens.map((mensagem) => (
                        <Typography key={this.nextKey}>- {mensagem}</Typography>
                    ))}
                </Box>
            );
        }

        return <Typography>Alterar o procurador da certidão</Typography>;
    }
}

export default inject('stores')(observer(ProcuradorCertidao));
